/**
 * Parent dashboard shell.
 *
 * Lists the signed-in owner's recovery profiles, or shows the empty state when
 * there are none. Data loads asynchronously: the renderer returns a container
 * immediately (with a skeleton) and fills it once the service resolves, so the
 * router stays synchronous.
 *
 * Each card offers the per-profile actions: edit contacts, rename, print a
 * recovery card, replace credentials, disable/enable (reversible — retrieval
 * stops but contacts are kept), and delete (permanent, with a confirmation step).
 * Most actions re-load the list on success so the card reflects the new state.
 *
 * Two of them are unusual and are explained where they are defined: printing a
 * card needs the phrase, which this page has never seen and the owner must type
 * back in; and replacing credentials produces values shown exactly once, so it
 * must *not* reload the list over the top of them.
 */

import { el, icon, fieldValue } from "../ui/dom.js";
import { pageHeader, errorAlert } from "../ui/patterns.js";
import { renderEmptyProfiles } from "./empty-profiles.js";
import { normalisePhrase, isCardPhrase } from "../ui/phrase.js";
import { printCardFor } from "../ui/print-card.js";
import { downloadPrintFile } from "../ui/print-pdf.js";
import { renderCredentialsPanel } from "../ui/credential-panel.js";
import { printPartnerNotice } from "../ui/print-partner-notice.js";
import { hasObviousPinPattern } from "../ui/pin-strength.js";

/** @type {import("../app/router.js").PageRenderer} */
export default function renderDashboard(context) {
  const frag = document.createDocumentFragment();
  frag.append(pageHeader("Your profiles", "Create a profile, then add the contacts it reveals."));

  const list = el("div", { class: "kin-stack" }, [
    el("wa-skeleton", { effect: "sheen" }),
    el("wa-skeleton", { effect: "sheen" }),
  ]);
  frag.append(list);

  /**
   * What an action can do to the page it was launched from: reload the list, or
   * replace it with a one-off panel. The second exists for reissued credentials,
   * which are shown once — a reload would wipe them before they can be written
   * down.
   *
   * @type {Surface}
   */
  const surface = {
    reload: () => loadProfiles(),
    show: (node) => list.replaceChildren(node),
  };

  /** (Re)load the profiles and render them into `list`. */
  function loadProfiles() {
    context.services.profiles
      .list()
      .then((profiles) => {
        if (profiles.length === 0) {
          list.replaceChildren(renderEmptyProfiles(context));
          return;
        }
        list.replaceChildren(
          el("div", { class: "kin-cta-row" }, [
            el("wa-button", { variant: "brand", href: "/profiles/new", dataset: { link: "" } }, [
              icon("plus"),
              "Create a profile",
            ]),
          ]),
          ...profiles.map((profile) => renderProfileCard(context, profile, surface)),
        );
      })
      .catch((error) => {
        // Render the failure inline rather than letting it become an unhandled
        // rejection. The global error boundary is a last resort for the unexpected;
        // a routine load failure (a dropped connection, a not-yet-propagated token)
        // belongs on the page, where the rest of the shell stays usable.
        list.replaceChildren(
          errorAlert(
            error instanceof Error
              ? error.message
              : "We could not load your profiles. Please try again.",
          ),
        );
      });
  }

  loadProfiles();
  return frag;
}

/**
 * @typedef {object} Surface
 * @property {() => void} reload Re-load the whole list after a change.
 * @property {(node: Node) => void} show Replace the list with a one-off panel.
 */

/**
 * @param {import("../app/router.js").RouteContext} context
 * @param {import("../types/models.js").RecoveryProfile} profile
 * @param {Surface} surface
 * @returns {HTMLElement}
 */
function renderProfileCard(context, profile, surface) {
  const reload = surface.reload;
  const count = profile.contacts.length;
  return el("wa-card", { class: "kin-profile-card" }, [
    el("div", { slot: "header", class: "kin-profile-head" }, [
      el("span", {}, [profile.label]),
      el("wa-badge", { variant: profile.enabled ? "success" : "neutral" }, [
        profile.enabled ? "Active" : "Disabled",
      ]),
    ]),
    el("p", { class: "kin-note" }, [count === 1 ? "1 contact" : `${count} contacts`]),
    el("div", { slot: "footer", class: "kin-profile-actions" }, [
      el(
        "wa-button",
        {
          appearance: "outlined",
          on: { click: () => context.navigate("/profiles/edit", { profileId: profile.id }) },
        },
        [icon("pen"), "Edit contacts"],
      ),
      el(
        "wa-button",
        {
          appearance: "plain",
          on: { click: () => openRenameDialog(context, profile, reload) },
        },
        [icon("pencil"), "Rename"],
      ),
      el(
        "wa-button",
        {
          appearance: "plain",
          on: { click: () => openPrintCardDialog(profile) },
        },
        [icon("print"), "Print card"],
      ),
      el(
        "wa-button",
        {
          appearance: "plain",
          on: { click: () => openReplaceCredentialsDialog(context, profile, surface) },
        },
        [icon("key"), "Replace credentials"],
      ),
      el(
        "wa-button",
        {
          appearance: "plain",
          on: { click: () => toggleEnabled(context, profile, reload) },
        },
        profile.enabled ? [icon("eye-slash"), "Disable"] : [icon("eye"), "Enable"],
      ),
      el(
        "wa-button",
        {
          appearance: "plain",
          variant: "danger",
          on: { click: () => openDeleteDialog(context, profile, reload) },
        },
        [icon("trash-can"), "Delete"],
      ),
    ]),
  ]);
}

/**
 * Rename a profile through a dialog.
 *
 * @param {import("../app/router.js").RouteContext} context
 * @param {import("../types/models.js").RecoveryProfile} profile
 * @param {() => void} reload
 * @returns {void}
 */
function openRenameDialog(context, profile, reload) {
  const nameField = el("wa-input", {
    label: "Profile name",
    value: profile.label,
    required: true,
  });
  const errorRegion = el("div", { "aria-live": "assertive" });

  const saveButton = el(
    "wa-button",
    {
      slot: "footer",
      variant: "brand",
      on: {
        click: async () => {
          const label = fieldValue(nameField);
          if (!label) {
            errorRegion.replaceChildren(errorAlert("Give the profile a name."));
            return;
          }
          errorRegion.replaceChildren();
          saveButton.setAttribute("loading", "");
          try {
            await context.services.profiles.rename(profile.id, label);
          } catch (error) {
            // Kept open on failure so the typed name and the reason both survive.
            saveButton.removeAttribute("loading");
            errorRegion.replaceChildren(errorAlert(messageFor(error)));
            return;
          }
          dialog.remove();
          reload();
        },
      },
    },
    ["Save"],
  );

  const dialog = el("wa-dialog", { label: "Rename profile" }, [
    el("div", { class: "kin-stack" }, [nameField, errorRegion]),
    el(
      "wa-button",
      { slot: "footer", appearance: "outlined", on: { click: () => dialog.remove() } },
      ["Cancel"],
    ),
    saveButton,
  ]);

  showDialog(dialog);
}

/**
 * Reprint a recovery card for a profile.
 *
 * The phrase is shown exactly once, at creation, and is not recoverable
 * afterwards — the server stores only its HMAC lookup, and this page has never
 * held it. So the owner types it back in. What they type is used to draw a card
 * and nothing else: it is **never sent anywhere**, and there is no service call
 * on this path at all. That is what makes an unverifiable, typed-in credential
 * acceptable here — a wrong phrase produces a card that does not work, which is
 * the owner's own problem to notice, not a security event.
 *
 * @param {import("../types/models.js").RecoveryProfile} profile
 * @returns {void}
 */
function openPrintCardDialog(profile) {
  const phraseField = el("wa-input", {
    label: "Recovery phrase",
    hint: "The four words shown when this profile was created.",
    autocomplete: "off",
    autocapitalize: "off",
    autocorrect: "off",
    spellcheck: "false",
    required: true,
  });
  const errorRegion = el("div", { "aria-live": "assertive" });

  const printButton = el(
    "wa-button",
    {
      slot: "footer",
      variant: "brand",
      on: {
        click: async () => {
          const phrase = normalisePhrase(fieldValue(phraseField));
          // Safe to name the problem precisely: nothing is looked up, so unlike
          // the retrieval form there is no attacker to hand an oracle to.
          if (!isCardPhrase(phrase)) {
            errorRegion.replaceChildren(
              errorAlert("A recovery phrase is four words, like otter-lantern-meadow-compass."),
            );
            return;
          }
          errorRegion.replaceChildren();
          // Close before printing. The dialog is modal, and leaving it open puts
          // it in the browser's top layer while the print stylesheet is hiding
          // the page around the card.
          dialog.remove();
          await printCardFor({ label: profile.label, phrase });
        },
      },
    },
    [icon("print"), "Print the card"],
  );

  // The other half of the same dialog: the same typed phrase, drawn as artwork a
  // commercial printer can use rather than sent to the local printer.
  //
  // Unlike printing, the dialog stays open until the file has been generated.
  // Removing it first would drop focus to the body with nothing announced, and
  // there would be no visible sign anything was happening during the wait.
  const downloadButton = el(
    "wa-button",
    {
      slot: "footer",
      appearance: "outlined",
      on: {
        click: async () => {
          const phrase = normalisePhrase(fieldValue(phraseField));
          if (!isCardPhrase(phrase)) {
            errorRegion.replaceChildren(
              errorAlert("A recovery phrase is four words, like otter-lantern-meadow-compass."),
            );
            return;
          }
          errorRegion.replaceChildren();
          downloadButton.setAttribute("loading", "");
          try {
            await downloadPrintFile({ label: profile.label, phrase });
          } catch (error) {
            downloadButton.removeAttribute("loading");
            errorRegion.replaceChildren(errorAlert(messageFor(error)));
            return;
          }
          downloadButton.removeAttribute("loading");
          dialog.remove();
        },
      },
    },
    [icon("print"), "Download print file (PDF)"],
  );

  const dialog = el("wa-dialog", { label: "Print a card" }, [
    el("div", { class: "kin-stack" }, [
      el("p", { class: "kin-prose" }, [
        "KinCode showed the recovery phrase for ",
        el("strong", {}, [profile.label]),
        " once and cannot show it again, so type it in to put it on a card.",
      ]),
      phraseField,
      el("p", { class: "kin-note" }, [
        "What you type here stays in this browser — it is used to draw the card " +
          "and is not sent to KinCode. If the phrase is wrong the card will not " +
          "work, and nothing will warn you.",
      ]),
      el("p", { class: "kin-note" }, [
        "If you no longer have the phrase, use Replace credentials instead.",
      ]),
      el("p", { class: "kin-note" }, [
        "“Print the card” uses your own printer. “Download print file” saves a PDF " +
          "sized for a printing company to produce proper cards from.",
      ]),
      printPartnerNotice(),
      errorRegion,
    ]),
    el(
      "wa-button",
      { slot: "footer", appearance: "outlined", on: { click: () => dialog.remove() } },
      ["Cancel"],
    ),
    downloadButton,
    printButton,
  ]);

  showDialog(dialog);
}

/**
 * Reissue a profile's phrase, PIN, or both.
 *
 * This is how a lost or photographed card is invalidated — the only way, since
 * a printed card cannot be recalled. Replacing the phrase changes the profile's
 * lookup, so every card already printed for it stops working; replacing the PIN
 * leaves cards working but means everyone holding one needs telling the new
 * number. The two are separate because the situations are: a card left on a bus
 * exposes the phrase and not the PIN.
 *
 * Contacts survive either reset. That is worth saying out loud in the dialog —
 * "replace credentials" sounds destructive and is not.
 *
 * @param {import("../app/router.js").RouteContext} context
 * @param {import("../types/models.js").RecoveryProfile} profile
 * @param {Surface} surface
 * @returns {void}
 */
function openReplaceCredentialsDialog(context, profile, surface) {
  // Defaults match the server's: a PIN reset is the ordinary case, and replacing
  // the phrase is opt-in because of what it does to printed cards.
  const pinSwitch = el("wa-switch", { checked: true }, ["Replace the PIN"]);
  const customPinField = el("wa-input", {
    label: "Choose your own PIN (optional)",
    hint: "Leave blank for a random PIN. Avoid repeating or sequential numbers.",
    name: "customPin",
    type: "password",
    inputmode: "numeric",
    maxlength: 6,
    autocomplete: "off",
    "password-toggle": true,
  });
  const phraseSwitch = el("wa-switch", {}, ["Replace the recovery phrase"]);
  const errorRegion = el("div", { "aria-live": "assertive" });

  const confirmButton = el(
    "wa-button",
    {
      slot: "footer",
      variant: "brand",
      on: {
        click: async () => {
          const pin = isChecked(pinSwitch);
          const phrase = isChecked(phraseSwitch);
          // Mirrors the server's own refusal so the round trip is not spent
          // learning something the page already knows.
          if (!pin && !phrase) {
            errorRegion.replaceChildren(errorAlert("Choose at least one to replace."));
            return;
          }
          const customPin = fieldValue(customPinField);
          if (customPin) {
            if (!/^\d{6}$/.test(customPin)) {
              errorRegion.replaceChildren(errorAlert("Enter a 6-digit PIN."));
              return;
            }
            // A cheap client-side echo of the server's own check (see
            // src/ui/pin-strength.js), so the obvious cases don't cost a round
            // trip. The server's blocklist is not mirrored here and still has
            // the final say — this is not the authoritative check.
            if (hasObviousPinPattern(customPin)) {
              errorRegion.replaceChildren(
                errorAlert("That PIN is too easy to guess. Avoid repeating or sequential numbers."),
              );
              return;
            }
          }
          errorRegion.replaceChildren();
          confirmButton.setAttribute("loading", "");
          /** @type {{ phrase?: string, pin?: string }} */
          let issued;
          try {
            /** @type {{ phrase: boolean, pin: boolean, customPin?: string }} */
            const options = { phrase, pin };
            if (customPin) {
              options.customPin = customPin;
            }
            issued = await context.services.profiles.regenerateCredentials(profile.id, options);
          } catch (error) {
            confirmButton.removeAttribute("loading");
            errorRegion.replaceChildren(errorAlert(messageFor(error)));
            return;
          }
          dialog.remove();
          // Shown, not reloaded. These values do not exist anywhere else and the
          // server will not repeat them, so re-rendering the list over the top
          // would destroy them.
          surface.show(
            renderCredentialsPanel({
              heading: `New credentials for “${profile.label}”.`,
              label: profile.label,
              phrase: issued.phrase,
              pin: issued.pin,
              intro:
                "This is the only time KinCode will show these. Write them down or " +
                "print a new card, and tell the people who may need them. Your " +
                "contacts have not changed.",
              actions: [
                el("wa-button", { variant: "brand", on: { click: () => surface.reload() } }, [
                  icon("arrow-right"),
                  "Back to your profiles",
                ]),
              ],
            }),
          );
        },
      },
    },
    [icon("key"), "Replace credentials"],
  );

  const dialog = el("wa-dialog", { label: "Replace credentials" }, [
    el("div", { class: "kin-stack" }, [
      el("p", { class: "kin-prose" }, [
        "Issue new credentials for ",
        el("strong", {}, [profile.label]),
        ". The contacts in this profile are kept — only the phrase and PIN used to " +
          "reach them change.",
      ]),
      pinSwitch,
      customPinField,
      el("p", { class: "kin-note" }, [
        "Use this if someone knows the PIN who should not. Printed cards keep working.",
      ]),
      phraseSwitch,
      el("p", { class: "kin-note" }, [
        "Use this if a card has been lost or photographed. Every card and QR code " +
          "already printed for this profile stops working, and you will be shown a " +
          "new phrase to print.",
      ]),
      errorRegion,
    ]),
    el(
      "wa-button",
      { slot: "footer", appearance: "outlined", on: { click: () => dialog.remove() } },
      ["Cancel"],
    ),
    confirmButton,
  ]);

  showDialog(dialog);
}

/**
 * Read a wa-switch. It exposes `checked` at runtime but types as a plain element.
 *
 * @param {Element} node
 * @returns {boolean}
 */
function isChecked(node) {
  return /** @type {{ checked?: boolean }} */ (/** @type {unknown} */ (node)).checked === true;
}

/**
 * Toggle a profile between active and disabled. Disabling stops retrieval but keeps
 * every contact — it is reversible.
 *
 * @param {import("../app/router.js").RouteContext} context
 * @param {import("../types/models.js").RecoveryProfile} profile
 * @param {() => void} reload
 * @returns {Promise<void>}
 */
async function toggleEnabled(context, profile, reload) {
  try {
    await context.services.profiles.setEnabled(profile.id, !profile.enabled);
    reload();
  } catch (error) {
    // Surface the failure inline at the top of the list rather than silently.
    const region = document.querySelector(".kin-content");
    if (region) {
      const alert = errorAlert(messageFor(error));
      region.prepend(alert);
      setTimeout(() => alert.remove(), 6000);
    }
  }
}

/**
 * Delete a profile after an explicit confirmation.
 *
 * Deletion is permanent and stops retrieval for these contacts, so — unlike
 * disable — it is guarded by a confirm step that spells out the consequence. The
 * brand palette renders `danger` as gold rather than red (docs/security-review.md),
 * so the wording, not the colour, carries the warning.
 *
 * @param {import("../app/router.js").RouteContext} context
 * @param {import("../types/models.js").RecoveryProfile} profile
 * @param {() => void} reload
 * @returns {void}
 */
function openDeleteDialog(context, profile, reload) {
  const errorRegion = el("div", { "aria-live": "assertive" });

  const confirmButton = el(
    "wa-button",
    {
      slot: "footer",
      variant: "danger",
      on: {
        click: async () => {
          errorRegion.replaceChildren();
          confirmButton.setAttribute("loading", "");
          try {
            await context.services.profiles.remove(profile.id);
          } catch (error) {
            confirmButton.removeAttribute("loading");
            errorRegion.replaceChildren(errorAlert(messageFor(error)));
            return;
          }
          dialog.remove();
          reload();
        },
      },
    },
    [icon("trash-can"), "Delete permanently"],
  );

  const dialog = el("wa-dialog", { label: "Delete this profile?" }, [
    el("div", { class: "kin-stack" }, [
      el("p", { class: "kin-prose" }, [
        "This permanently deletes ",
        el("strong", {}, [profile.label]),
        " and all of its contacts. Anyone using its phrase and PIN will no longer be " +
          "able to reach them. This cannot be undone.",
      ]),
      el("p", { class: "kin-note" }, [
        "To keep the contacts but stop retrieval, use Disable instead — it can be turned " +
          "back on at any time.",
      ]),
      errorRegion,
    ]),
    el(
      "wa-button",
      { slot: "footer", appearance: "outlined", on: { click: () => dialog.remove() } },
      ["Cancel"],
    ),
    confirmButton,
  ]);

  showDialog(dialog);
}

/**
 * Append a dialog to the document and open it. Mirrors the pattern in
 * `edit-contacts.js`: wa-dialog exposes an `open` property at runtime.
 *
 * @param {HTMLElement} dialog
 * @returns {void}
 */
function showDialog(dialog) {
  document.body.append(dialog);
  // @ts-expect-error wa-dialog exposes an `open` property at runtime.
  dialog.open = true;
}

/**
 * @param {unknown} error
 * @returns {string}
 */
function messageFor(error) {
  return error instanceof Error ? error.message : "Something went wrong. Please try again.";
}
